// components/Bookshelf.tsx — the bookshelf surface.
//
// Shown when the user opens it via the header menu. Lists every story
// grouped by scenario; each row renders a templated cover plus three
// actions: Read (opens BookReader), Continue playing (switches to the
// story and closes the bookshelf), and Share (Phase 1 placeholder —
// disabled with a "coming soon" tooltip).
//
// Lazy-loads chapter counts per story (HEAD-of-sorts: just the COUNT,
// not the bodies). Counts can race in mid-game when a new chapter lands
// from the per-turn fire-and-forget; the bookshelf is recoverable —
// closing and reopening refetches.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import BookCover from "./BookCover";
import { chaptersQuery } from "../services/booksApi";
import type { StoryListEntry, StoryStatus } from "../services/storiesApi";
import { SCENARIO_PRESENTATIONS } from "../scenarios/presentation";
import ShareModal from "./ShareModal";

export interface BookshelfProps {
  /** All stories belonging to the user (active + finished). */
  stories: StoryListEntry[];
  /** Player display name (the user's name). Goes onto every cover as "by". */
  authorName: string;
  /** Open the BookReader for the given story id. */
  onRead: (storyId: string) => void;
  /** Switch the active story and return to game. */
  onContinuePlaying: (storyId: string) => void;
  /** Close the bookshelf (return to whatever was underneath). */
  onClose: () => void;
}

type ChapterCountMap = Record<string, number | undefined>;

function relativeTime(
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  try {
    const ts = new Date(iso).getTime();
    if (!Number.isFinite(ts)) return "";
    const diffMs = Date.now() - ts;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return t("bookshelf:bookshelf.relative.moment");
    const min = Math.floor(sec / 60);
    if (min < 60) return t("bookshelf:bookshelf.relative.minute", { count: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return t("bookshelf:bookshelf.relative.hour", { count: hr });
    const day = Math.floor(hr / 24);
    if (day < 30) return t("bookshelf:bookshelf.relative.day", { count: day });
    const mo = Math.floor(day / 30);
    if (mo < 12) return t("bookshelf:bookshelf.relative.month", { count: mo });
    const yr = Math.floor(mo / 12);
    return t("bookshelf:bookshelf.relative.year", { count: yr });
  } catch {
    return "";
  }
}

function statusPillLabel(
  status: StoryStatus,
  chapterCount: number | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (status === "finished") return t("bookshelf:bookshelf.status.finished");
  if (chapterCount === undefined) return t("bookshelf:bookshelf.status.inProgress");
  if (chapterCount === 0) return t("bookshelf:bookshelf.status.building");
  return t("bookshelf:bookshelf.status.chapter", { count: chapterCount });
}

export default function Bookshelf({
  stories,
  authorName,
  onRead,
  onContinuePlaying,
  onClose,
}: BookshelfProps): JSX.Element {
  const { t } = useTranslation(["bookshelf", "scenarios"]);
  const [chapterCounts, setChapterCounts] = useState<ChapterCountMap>({});
  const [hoveredStoryId, setHoveredStoryId] = useState<string | null>(null);
  const [shareStoryId, setShareStoryId] = useState<string | null>(null);

  // Fetch chapter counts for every story in parallel on mount + when the
  // story list changes. Best-effort — a failed call just leaves the
  // count undefined (renders as "In progress" / "Building…").
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        stories.map(async (s) => {
          try {
            const res = await chaptersQuery(s.id);
            return [s.id, res.chapters.length] as const;
          } catch {
            return [s.id, undefined] as const;
          }
        }),
      );
      if (cancelled) return;
      const next: ChapterCountMap = {};
      for (const [id, count] of entries) next[id] = count;
      setChapterCounts(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [stories]);

  // Group by scenarioId, preserving the catalogue order from SCENARIO_PRESENTATIONS.
  const grouped = useMemo(() => {
    const scenarioOrder = Object.keys(SCENARIO_PRESENTATIONS);
    const byScenario: Record<string, StoryListEntry[]> = {};
    for (const s of stories) {
      if (!byScenario[s.scenarioId]) byScenario[s.scenarioId] = [];
      byScenario[s.scenarioId]!.push(s);
    }
    // Sort each group by lastPlayedAt desc so newest book in each scenario
    // shows first.
    for (const arr of Object.values(byScenario)) {
      arr.sort(
        (a, b) =>
          new Date(b.lastPlayedAt).getTime() -
          new Date(a.lastPlayedAt).getTime(),
      );
    }
    const orderedScenarioIds = [
      ...scenarioOrder.filter((id) => byScenario[id]),
      ...Object.keys(byScenario).filter((id) => !scenarioOrder.includes(id)),
    ];
    return orderedScenarioIds.map((id) => ({
      scenarioId: id,
      // Prefer translated label; fall back to structural pillLabel or id.
      label: t(`scenarios:${id}.pillLabel`, {
        defaultValue: SCENARIO_PRESENTATIONS[id]?.pillLabel ?? id,
      }),
      books: byScenario[id] ?? [],
    }));
  }, [stories, t]);

  // ── Styles ──────────────────────────────────────────────────────────
  const shell: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "#0a0706",
    color: "#e8d9b8",
    fontFamily:
      "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
    display: "flex",
    flexDirection: "column",
    zIndex: 70,
    overflow: "hidden",
  };
  const header: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "20px 40px",
    borderBottom: "1px solid rgba(212,165,116,0.18)",
    background: "rgba(20,14,10,0.55)",
  };
  const title: CSSProperties = {
    margin: 0,
    fontSize: "1.6rem",
    fontWeight: 500,
    color: "#d4a574",
    letterSpacing: "0.02em",
    flex: 1,
  };
  const subtitle: CSSProperties = {
    margin: 0,
    fontSize: "0.82rem",
    fontStyle: "italic",
    color: "#8a7355",
  };
  const closeBtn: CSSProperties = {
    background: "transparent",
    color: "#8a7355",
    border: "1px solid rgba(212,165,116,0.18)",
    padding: "6px 14px",
    fontSize: "0.78rem",
    fontFamily: "'JetBrains Mono', monospace",
    cursor: "pointer",
    borderRadius: 2,
  };
  const body: CSSProperties = {
    flex: 1,
    overflowY: "auto",
    padding: "40px 40px 80px",
  };
  const sectionHeader: CSSProperties = {
    fontSize: "0.78rem",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "#8a7355",
    fontFamily: "'JetBrains Mono', monospace",
    margin: "32px 0 16px",
  };
  const grid: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 28,
  };
  const card: CSSProperties = {
    background: "rgba(20,14,10,0.55)",
    border: "1px solid rgba(212,165,116,0.18)",
    borderRadius: 4,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    position: "relative",
  };
  const cardMeta: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  };
  const cardTitle: CSSProperties = {
    margin: 0,
    fontSize: "1.05rem",
    fontWeight: 500,
    color: "#e8d9b8",
  };
  const cardSubtitle: CSSProperties = {
    margin: 0,
    fontSize: "0.78rem",
    color: "#8a7355",
    fontStyle: "italic",
  };
  const cardRow: CSSProperties = {
    display: "flex",
    gap: 8,
    alignItems: "center",
  };
  const pill: CSSProperties = {
    padding: "2px 10px",
    border: "1px solid rgba(212,165,116,0.25)",
    borderRadius: 2,
    fontSize: "0.7rem",
    fontFamily: "'JetBrains Mono', monospace",
    color: "#d4a574",
    letterSpacing: "0.04em",
  };
  const lastActivity: CSSProperties = {
    fontSize: "0.72rem",
    color: "#8a7355",
    marginLeft: "auto",
  };
  const actionsRow: CSSProperties = {
    display: "flex",
    gap: 8,
  };
  const actionBtn: CSSProperties = {
    flex: 1,
    background: "transparent",
    color: "#d4a574",
    border: "1px solid rgba(212,165,116,0.45)",
    padding: "8px 0",
    fontSize: "0.8rem",
    fontFamily:
      "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
    cursor: "pointer",
    borderRadius: 2,
  };
  const empty: CSSProperties = {
    margin: "80px auto",
    maxWidth: 460,
    textAlign: "center",
    color: "#8a7355",
    fontSize: "0.95rem",
    fontStyle: "italic",
  };

  return (
    <div style={shell}>
      <header style={header}>
        <div>
          <h1 style={title}>{t("bookshelf:bookshelf.title")}</h1>
          <p style={subtitle}>{t("bookshelf:bookshelf.subtitle")}</p>
        </div>
        <button
          type="button"
          style={closeBtn}
          onClick={onClose}
          aria-label={t("bookshelf:bookshelf.closeAria")}
        >
          {t("bookshelf:bookshelf.close")}
        </button>
      </header>

      {shareStoryId && (() => {
        const story = stories.find((s) => s.id === shareStoryId);
        if (!story) return null;
        const presentation = SCENARIO_PRESENTATIONS[story.scenarioId];
        const theme = presentation?.theme;
        return (
          <ShareModal
            storyId={story.id}
            storyName={story.name || t("bookshelf:bookReader.untitled")}
            authorName={authorName}
            scenarioName={t(`scenarios:${story.scenarioId}.pillLabel`, {
              defaultValue: presentation?.pillLabel ?? story.scenarioId,
            })}
            accent={theme?.accent ?? "#d4a574"}
            accentMuted={theme?.accentMuted ?? "rgba(212,165,116,0.45)"}
            ink={theme?.ink ?? "#e8d9b8"}
            inkMuted={theme?.inkMuted ?? "#8a7355"}
            paper={theme?.paper ?? "rgba(20,14,10,0.55)"}
            paperEdge={theme?.paperEdge ?? "rgba(212,165,116,0.18)"}
            fontFamily={
              theme?.fontFamily ??
              "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif"
            }
            onClose={() => setShareStoryId(null)}
          />
        );
      })()}

      <div style={body}>
        {stories.length === 0 ? (
          <div style={empty}>{t("bookshelf:bookshelf.empty")}</div>
        ) : (
          grouped.map(({ scenarioId, label, books }) => (
            <section key={scenarioId}>
              <h2 style={sectionHeader}>{label}</h2>
              {books.length === 0 ? (
                <div
                  style={{
                    color: "#8a7355",
                    fontStyle: "italic",
                    fontSize: "0.85rem",
                    marginBottom: 16,
                  }}
                >
                  {t("bookshelf:bookshelf.emptyGroup")}
                </div>
              ) : (
                <div style={grid}>
                  {books.map((book) => {
                    const count = chapterCounts[book.id];
                    const showActions = hoveredStoryId === book.id;
                    return (
                      <div
                        key={book.id}
                        style={card}
                        onMouseEnter={() => setHoveredStoryId(book.id)}
                        onMouseLeave={() =>
                          setHoveredStoryId((cur) =>
                            cur === book.id ? null : cur,
                          )
                        }
                        onFocus={() => setHoveredStoryId(book.id)}
                      >
                        <div style={{ alignSelf: "center" }}>
                          <BookCover
                            scenarioId={book.scenarioId}
                            storyId={book.id}
                            title={book.name || t("bookshelf:bookReader.untitled")}
                            authorName={authorName}
                            chapterCount={count ?? 0}
                            storyStatus={book.status}
                            onClick={() => onRead(book.id)}
                          />
                        </div>
                        <div style={cardMeta}>
                          <h3 style={cardTitle}>
                            {book.name || t("bookshelf:bookReader.untitled")}
                          </h3>
                          <p style={cardSubtitle}>
                            {t("bookshelf:bookshelf.by", { name: authorName })}
                          </p>
                        </div>
                        <div style={cardRow}>
                          <span style={pill}>
                            {statusPillLabel(book.status, count, t)}
                          </span>
                          <span style={lastActivity}>
                            {relativeTime(book.lastPlayedAt, t)}
                          </span>
                        </div>
                        <div
                          style={{
                            ...actionsRow,
                            opacity: showActions ? 1 : 0.75,
                            transition: "opacity 0.18s",
                          }}
                        >
                          <button
                            type="button"
                            style={actionBtn}
                            onClick={() => onRead(book.id)}
                            disabled={count === 0}
                            title={
                              count === 0
                                ? t("bookshelf:bookshelf.actions.readDisabled")
                                : undefined
                            }
                          >
                            {t("bookshelf:bookshelf.actions.read")}
                          </button>
                          <button
                            type="button"
                            style={actionBtn}
                            onClick={() => onContinuePlaying(book.id)}
                          >
                            {t("bookshelf:bookshelf.actions.continue")}
                          </button>
                          <button
                            type="button"
                            style={actionBtn}
                            onClick={() => setShareStoryId(book.id)}
                            disabled={count === 0}
                            title={
                              count === 0
                                ? t("bookshelf:bookshelf.actions.shareDisabled")
                                : undefined
                            }
                          >
                            {t("bookshelf:bookshelf.actions.share")}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          ))
        )}
      </div>
    </div>
  );
}
